// Shared constants + helpers for event compliance attachments. Imported by the
// uploader (advisor side) and the reviewer list so file limits, icons, and size
// formatting stay identical across both surfaces.

import { FiFile, FiFileText, FiImage, FiMonitor } from 'react-icons/fi';
import { attachmentKind } from '../../services/api';

// Per-file ceiling. Kept under Mongo's 16MB BSON document limit (the bytes are
// stored as Binary in a single doc, mirroring the cover-photo asset).
export const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024; // 15MB
export const MAX_ATTACHMENTS = 10;

// Accepted upload types: PDFs, PowerPoint (.ppt/.pptx), and images.
export const ACCEPTED_ATTACHMENT_TYPES = [
  'application/pdf',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'image/png',
  'image/jpeg',
  'image/jpg',
];

// `accept` attribute for the file input — types plus extensions so the OS file
// picker filters correctly even when a browser reports an empty MIME type.
export const ATTACHMENT_ACCEPT = [...ACCEPTED_ATTACHMENT_TYPES, '.pdf', '.ppt', '.pptx', '.png', '.jpg', '.jpeg'].join(',');

const KIND_ICON = {
  image: FiImage,
  pdf: FiFileText,
  ppt: FiMonitor,
  other: FiFile,
};

/** Return the react-icons component type for an attachment's kind. */
export const iconForAttachment = (contentType, name) => KIND_ICON[attachmentKind(contentType, name)] || FiFile;

/** Human-readable file size, e.g. "2.4 MB". */
export const formatFileSize = (bytes) => {
  const n = Number(bytes);
  if (!n || n < 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

/** Friendly label for a rejected file type. */
export const isAcceptedAttachmentType = (file) => {
  if (file?.type && ACCEPTED_ATTACHMENT_TYPES.includes(file.type)) return true;
  const ext = String(file?.name || '').toLowerCase().split('.').pop();
  return ['pdf', 'ppt', 'pptx', 'png', 'jpg', 'jpeg'].includes(ext);
};
